/**
 * Cache entry shape stored in memory.
 * `expiresAt` uses epoch milliseconds for quick expiration checks.
 */
type CacheEntry<T> = {
  /** The cached value */
  value: T;
  /** Expiration timestamp in epoch milliseconds */
  expiresAt: number;
};

/**
 * Five minutes in milliseconds, used as a default TTL for cached entries.
 */
const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * In-memory cache map.
 * Keys should be stable, deterministic identifiers (e.g., `users.current:${userId}`).
 */
const memoryCache = new Map<string, CacheEntry<unknown>>();

/**
 * Default cache TTL (time-to-live) in milliseconds.
 * 5 minutes balances freshness with reduced network usage.
 */
export const DEFAULT_TTL_MS = FIVE_MINUTES_MS;

/**
 * Internal cache read with expiration check.
 * Returns `null` for missing/expired entries.
 */
const getCachedValue = <T>(key: string): T | null => {
  // Check for entry existence
  const entry = memoryCache.get(key);

  // If no entry, return null
  if (!entry) return null;

  // If current time exceeds expiration, invalidate cache and return null
  if (Date.now() > entry.expiresAt) {
    memoryCache.delete(key);
    return null;
  }

  // Return cached value if valid
  return entry.value as T;
};

/**
 * Internal cache write.
 * Stores a value with an expiration timestamp based on `ttlMs`.
 */
const setCachedValue = <T>(key: string, value: T, ttlMs: number) => {
  memoryCache.set(key, { value, expiresAt: Date.now() + ttlMs });
};

/**
 * Read-through cache helper.
 * Returns cached value when fresh; otherwise calls `fetcher`, caches, and returns it.
 */
export const cachedGet = async <T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
): Promise<T> => {
  // Attempt to read from cache first
  const cached = getCachedValue<T>(key);

  // If cached value exists and is valid, return it immediately
  if (cached !== null) return cached;

  // Otherwise, fetch fresh data, cache it, and return
  const value = await fetcher();
  setCachedValue(key, value, ttlMs);
  return value;
};

/**
 * Clears cache entries whose keys start with the provided prefix.
 * Useful after mutations to invalidate related GET responses.
 */
export const clearCacheByPrefix = (prefix: string) => {
  for (const key of memoryCache.keys()) { // Iterate over cache keys
    if (key.startsWith(prefix)) { // Check if key matches the prefix
      memoryCache.delete(key); // Invalidate cache entry
    }
  }
};
